import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './Hero.css';
import './Demo.css';

const Hero = () => {
  const navigate = useNavigate();
  const [typedIndex, setTypedIndex] = useState(1);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const codeLines = [
    '# Problem: Design an LRU Cache with O(1) Get and Put operations',
    'class Node:',
    '    def __init__(self, key: int, val: int):',
    '        self.key, self.val = key, val',
    '        self.prev = self.next = None',
    '',
    'class LRUCache:',
    '    def __init__(self, capacity: int):',
    '        self.cap = capacity',
    '        self.cache = {} # Map key -> Node pointer',
    '        self.head, self.tail = Node(0, 0), Node(0, 0)',
    '        self.head.next, self.tail.prev = self.tail, self.head',
    '',
    '    def get(self, key: int) -> int:',
    '        if key in self.cache:',
    '            self._remove(self.cache[key])',
    '            self._insert(self.cache[key])',
    '            return self.cache[key].val',
    '        return -1',
    '',
    '    def put(self, key: int, value: int) -> None:',
    '        if key in self.cache: self._remove(self.cache[key])',
    '        node = Node(key, value)',
    '        self.cache[key] = node',
    '        self._insert(node)',
    '        if len(self.cache) > self.cap:',
    '            lru = self.head.next',
    '            self._remove(lru)',
    '            del self.cache[lru.key]'
  ];

  const answerBulletPoints = [
    {
      title: 'Hash Map + Doubly Linked List',
      desc: 'Hash Map provides O(1) key-to-Node pointer lookups.'
    },
    {
      title: 'Doubly Linked List for Recency',
      desc: 'Maintains temporal ordering. Head = Most Recently Used, Tail = Least Recently Used.'
    },
    {
      title: 'O(1) Get Operation',
      desc: 'Retrieve Node reference from Map, detach from current position, move to Head.'
    },
    {
      title: 'O(1) Put Operation',
      desc: 'Insert at Head; if capacity exceeded, evict Tail node in O(1) and remove key from Map.'
    }
  ];

  // Auto-advance bullet points typing animation
  useEffect(() => {
    const timer = setInterval(() => {
      setTypedIndex((prev) => (prev < answerBulletPoints.length ? prev + 1 : 1));
    }, 2600);
    return () => clearInterval(timer);
  }, [answerBulletPoints.length]);

  // Interactive Drag Slider Handlers
  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global window listeners when user drags the slider
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // Authentic Google Meet Screen Sharing UI
  const renderGoogleMeetScreen = () => (
    <div className="gmeet-ui-container">
      {/* Top Banner: Authentic Google Meet Screen Share Notification */}
      <div className="gmeet-presenting-banner">
        <div className="presenting-banner-left">
          <svg className="meet-icon-svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
          </svg>
          <span className="presenting-text">You're presenting to everyone</span>
        </div>
        <button className="gmeet-stop-present-btn">Stop presenting</button>
      </div>

      {/* Main Stage: Shared Code Editor + Interviewer Video Sidebar */}
      <div className="gmeet-stage-area">
        {/* Shared VS Code Editor Window */}
        <div className="editor-window">
          <div className="editor-top-bar">
            <div className="window-dots">
              <span className="w-dot dot-red"></span>
              <span className="w-dot dot-yellow"></span>
              <span className="w-dot dot-green"></span>
            </div>
            <div className="editor-tab active">
              <span className="tab-icon" style={{ fontSize: '0.72rem', fontWeight: 700, color: '#38bdf8' }}>py</span>
              <span>LRU_Cache.py</span>
              <span className="tab-close">×</span>
            </div>
            <div className="editor-title-right">Python 3 • UTF-8</div>
          </div>
          
          <div className="editor-code-body">
            <div className="code-gutter">
              {codeLines.map((_, i) => (
                <span key={i}>{i + 1}</span>
              ))}
            </div>
            <pre className="code-text-content">
              <code>{codeLines.map((line, idx) => {
                const isComment = line.trim().startsWith('#');
                const isDef = line.includes('def ') || line.includes('class ');
                return (
                  <div key={idx} className={`code-line ${isComment ? 'comment' : isDef ? 'definition' : ''}`}>
                    {line}
                  </div>
                );
              })}</code>
            </pre>
          </div>

          <div className="editor-status-bar">
            <span>Spaces: 4</span>
            <span>UTF-8</span>
            <span>Python 3.12</span>
            <span>Ln 14, Col 28</span>
          </div>
        </div>

        {/* Right Video Sidebar: Interviewer (Audio Active) & Candidate Tile */}
        <div className="gmeet-video-sidebar">
          {/* Interviewer Tile */}
          <div className="gmeet-video-tile interviewer-tile">
            <div className="interviewer-cam-off-view">
              <div className="tile-avatar interviewer-avatar speaking-pulse">
                <span>DM</span>
              </div>
            </div>

            <div className="tile-speaking-indicator">
              <span></span><span></span><span></span>
            </div>

            <div className="tile-bottom-meta">
              <svg className="tile-mic-icon" viewBox="0 0 24 24" width="13" height="13" fill="#34a853" style={{ marginRight: '4px' }}>
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              <span className="tile-name">David Miller (VP @ Stripe)</span>
            </div>
          </div>

          {/* Candidate Tile */}
          <div className="gmeet-video-tile candidate-tile">
            <div className="tile-avatar candidate-avatar">YOU</div>
            <div className="tile-bottom-meta">
              <svg className="tile-mic-icon" viewBox="0 0 24 24" width="14" height="14" fill="#ffffff">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              <span className="tile-name">You (Presenting)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Google Meet Bottom Controls Bar */}
      <div className="gmeet-bottom-bar">
        <div className="gmeet-bottom-left">
          <span className="gmeet-clock">10:45 AM</span>
          <span className="gmeet-divider">|</span>
          <span className="gmeet-code">stripe-swe-982</span>
        </div>

        <div className="gmeet-center-controls">
          <button className="gmeet-btn-circle" title="Microphone Active">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>
          </button>
          <button className="gmeet-btn-circle" title="Camera">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M15 8v8H5V8h10m1-2H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4V7c0-.55-.45-1-1-1z"/>
            </svg>
          </button>
          <button className="gmeet-btn-circle" title="Captions Active">
            <span className="meet-cc-icon">CC</span>
          </button>
          <button className="gmeet-btn-circle" title="Reactions">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
              <line x1="9" y1="9" x2="9.01" y2="9"/>
              <line x1="15" y1="9" x2="15.01" y2="9"/>
            </svg>
          </button>
          <button className="gmeet-btn-circle active-presenting" title="You are presenting your screen">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
            </svg>
          </button>
          <button className="gmeet-btn-circle" title="Raise hand">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v3"/>
              <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v7"/>
              <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/>
              <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
            </svg>
          </button>
          <button className="gmeet-btn-circle" title="More options">
            <span className="meet-dots-icon">⋮</span>
          </button>
          <button className="gmeet-btn-pill-end" title="Leave call">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="#ffffff">
              <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.996.996 0 0 1 0-1.41C3.28 8.91 7.42 7.5 12 7.5s8.72 1.41 11.71 4.17c.39.39.39 1.02 0 1.41l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
            </svg>
          </button>
        </div>

        <div className="gmeet-bottom-right">
          <button className="gmeet-side-icon" title="Meeting details">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </button>
          <button className="gmeet-side-icon" title="People (2)">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span className="side-badge">2</span>
          </button>
          <button className="gmeet-side-icon" title="In-call chat">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          <button className="gmeet-side-icon" title="Host controls">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <section className="hero-section">
      <div className="hero-container">
        
        {/* Hero Header & Value Proposition */}
        <motion.div 
          className="hero-header"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="hero-eyebrow-badge">
            <span className="eyebrow-dot"></span>
            <span>AI INTERVIEW COPILOT</span>
          </div>

          <h1 className="hero-title">
            <span className="hero-title-main">Crack Interviews.</span>
            <br />
            <span className="hero-title-highlight">In Real Time.</span>
          </h1>

          <p className="hero-subtitle">
            Instant code solutions, algorithms, and system design streamed live. Zero lag. 100% undetectable.
          </p>

          {/* Action Row */}
          <div className="hero-cta-group">
            <button 
              className="btn-hero-primary"
              onClick={() => {
                const token = localStorage.getItem('token');
                navigate(token ? '/dashboard' : '/login');
              }}
            >
              <span>{localStorage.getItem('token') ? 'Go to Dashboard' : 'Get Started Free'}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </button>

            <button 
              className="btn-hero-secondary"
              onClick={() => {
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polygon points="10 8 16 12 10 16 10 8"></polygon>
              </svg>
              <span>See How It Works</span>
            </button>
          </div>

          {/* Key Value & Performance Metrics Strip */}
          <div className="hero-metrics-bar">
            <div className="metric-item">
              <span className="metric-val">&lt; 180ms</span>
              <span className="metric-lbl">Latency</span>
            </div>
            <div className="metric-divider"></div>
            <div className="metric-item">
              <span className="metric-val">100%</span>
              <span className="metric-lbl">Stealth</span>
            </div>
            <div className="metric-divider"></div>
            <div className="metric-item">
              <span className="metric-val">70+</span>
              <span className="metric-lbl">Tech Stacks</span>
            </div>
            <div className="metric-divider"></div>
            <div className="metric-item">
              <span className="metric-val">Universal</span>
              <span className="metric-lbl">Meet &amp; Zoom</span>
            </div>
          </div>
        </motion.div>

        {/* Stealth View Comparison Preset Controls */}
        <div className="hero-slider-presets-bar">
          <span className="hero-presets-label">STEALTH COMPARISON:</span>
          <div className="hero-presets-btn-group">
            <button 
              className={`hero-preset-btn ${sliderPosition > 85 ? 'active' : ''}`}
              onClick={() => setSliderPosition(100)}
            >
              What Interviewer Sees (Clean)
            </button>
            <button 
              className={`hero-preset-btn ${sliderPosition >= 40 && sliderPosition <= 60 ? 'active' : ''}`}
              onClick={() => setSliderPosition(50)}
            >
              Split View (50/50)
            </button>
            <button 
              className={`hero-preset-btn ${sliderPosition < 15 ? 'active' : ''}`}
              onClick={() => setSliderPosition(0)}
            >
              What You See (Copilot Active)
            </button>
          </div>
        </div>

        {/* Hero Interactive Stage (Live Video Conference + CrackIT Desktop Overlay) */}
        <motion.div 
          className="hero-stage-wrapper"
          initial={{ opacity: 0, y: 36, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.85, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mac-conference-window">
            
            {/* macOS Window Topbar */}
            <div className="mac-window-topbar">
              <div className="mac-window-dots">
                <span className="dot dot-close"></span>
                <span className="dot dot-minimize"></span>
                <span className="dot dot-expand"></span>
              </div>
              <div className="mac-window-title">
                <span>Google Meet • Senior Software Engineer Interview</span>
              </div>
              <div className="mac-window-actions">
                <span className="live-rec-badge"><span className="rec-dot"></span>REC</span>
                <span className="participants-count">2 Participants</span>
                <span className="stealth-shield-badge">STEALTH ACTIVE</span>
              </div>
            </div>

            {/* Main Video Call Canvas with Interactive Split Slider */}
            <div 
              className="conference-video-stage interactive-slider-container"
              ref={containerRef}
              onMouseDown={(e) => {
                setIsDragging(true);
                handleMove(e.clientX);
              }}
              onTouchStart={(e) => {
                setIsDragging(true);
                handleMove(e.touches[0].clientX);
              }}
            >
              
              {/* ========================================================
                  LAYER 1 (BASE): WHAT THE INTERVIEWER SEES (100% Clean)
                 ======================================================== */}
              <div className="hero-slider-layer layer-interviewer">
                <div className="hero-layer-badge badge-clean">
                  <span className="badge-dot green"></span>
                  <span>WHAT INTERVIEWER SEES (100% CLEAN)</span>
                </div>

                {renderGoogleMeetScreen()}
              </div>

              {/* ========================================================
                  LAYER 2 (TOP): WHAT YOU SEE (CrackIT HUD Overlay Active)
                 ======================================================== */}
              <div 
                className="hero-slider-layer layer-candidate"
                style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
              >
                <div className="hero-layer-badge badge-copilot">
                  <span className="badge-dot cyan"></span>
                  <span>WHAT YOU SEE (LIVE CRACKIT HUD)</span>
                </div>

                {renderGoogleMeetScreen()}

                {/* =========================================================
                    AUTHENTIC CRACKIT DESKTOP APP OVERLAY (desktop-v1)
                   ========================================================= */}
                <div className="hero-app-container-overlay">
                  
                  {/* Titlebar */}
                  <div className="hero-overlay-titlebar">
                    <div className="hero-overlay-titlebar-text">
                      <div className="hero-overlay-status-dot"></div>
                      <span className="hero-overlay-header-title">CRACKIT AI //</span>
                      <span className="hero-overlay-status-label">LIVE ASSISTANT</span>
                    </div>
                    <div className="hero-overlay-right-controls">
                      <div className="hero-overlay-stealth-hint-badge" title="Stealth Hotkey">
                        <span className="stealth-key">HIDE:</span> ⌘⇧H
                      </div>
                      <div className="hero-overlay-time-badge">
                        <span>45:18</span>
                      </div>
                      <button className="hero-overlay-icon-btn" aria-label="Settings">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                      </button>
                      <button className="hero-overlay-minimize-btn">-</button>
                    </div>
                  </div>

                  {/* Control Bar */}
                  <div className="hero-overlay-control-bar">
                    <div className="hero-overlay-ask-input-box">
                      <span className="ask-ai-label">Ask AI:</span>
                      <input 
                        type="text" 
                        className="ask-ai-input" 
                        readOnly 
                        placeholder="Type a question & press Enter..." 
                      />
                    </div>
                    <div className="hero-overlay-action-group">
                      <button className="hero-action-btn btn-analyze">Analyze Screen</button>
                      <button className="hero-action-btn btn-end">End</button>
                    </div>
                  </div>

                  {/* Scrollable Feed Viewport */}
                  <div className="hero-overlay-feed-viewport">
                    
                    {/* Interviewer Question Block */}
                    <div className="hero-qa-block qa-question-block">
                      <div className="hero-question-meta">
                        <span className="speaker-tag-interviewer">INTERVIEWER</span>
                        <span className="interviewer-name-str">David Miller • Stripe</span>
                        <span className="speech-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '4px' }}>
                            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                            <line x1="12" x2="12" y1="19" y2="22"/>
                          </svg>
                          Audio Detected
                        </span>
                      </div>
                      <div className="hero-question-text">
                        "How do you design an LRU Cache with O(1) get and put operations? Walk me through the data structures and edge cases."
                      </div>
                    </div>

                    {/* CrackIt Copilot Answer Block */}
                    <div className="hero-qa-block qa-answer-block">
                      <div className="hero-answer-meta">
                        <div className="hero-copilot-badge">
                          <span className="bolt-icon">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                            </svg>
                          </span>
                          <span>CRACKIT COPILOT</span>
                        </div>
                        <span className="model-tag">GPT-4o Omniverse • 180ms</span>
                      </div>

                      <div className="hero-answer-bullets">
                        {answerBulletPoints.map((bullet, idx) => (
                          <div 
                            key={idx} 
                            className={`hero-bullet-row ${idx < typedIndex ? 'bullet-visible' : 'bullet-pending'}`}
                          >
                            <span className="bullet-dot">•</span>
                            <span className="bullet-content">
                              <strong className="bullet-headline">{bullet.title}:</strong> {bullet.desc}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="hero-code-snippet">
                        <div className="code-header">
                          <span>LRU_Cache.py</span>
                          <span>Python 3</span>
                        </div>
                        <pre><code>{`class LRUCache:
    def __init__(self, capacity: int):
        self.cap, self.cache = capacity, {}
        self.head, self.tail = Node(0, 0), Node(0, 0)
        self.head.next, self.tail.prev = self.tail, self.head`}</code></pre>
                      </div>

                      <div className="hero-protip-callout">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                          <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                          <path d="M9 18h6"/>
                          <path d="M10 22h4"/>
                        </svg>
                        <strong>Pro-Tip:</strong> Mention dummy head and tail nodes to avoid null pointer checks during node removal.
                      </div>
                    </div>

                  </div>

                </div>

              </div>

              {/* ========================================================
                  DRAGGABLE SLIDER DIVIDER HANDLE
                 ======================================================== */}
              <div 
                className="hero-slider-divider"
                style={{ left: `${sliderPosition}%` }}
              >
                <div className="hero-divider-line"></div>
                <div 
                  className="hero-handle-knob" 
                  title="Drag to compare stealth"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                >
                  <span className="hero-knob-arrows">⟷</span>
                  <span className="hero-knob-text">DRAG</span>
                </div>
                <div className="hero-divider-line"></div>
              </div>

            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Hero;
